<script setup>
import {computed} from "vue";
import {useRoute} from "vue-router";
import {useAuthService} from "@/composables/useAuthService";
import {useValidation} from "@/composables/useValidation";
import {useTimelineModels} from "@/composables/useTimelineModels";
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";

// Big "vs" fixture card for the timeline feed's slim TimelineFixture (mockups
// .card.fixture): meta chips, a vertical vs-row with the time/score stacked in
// the middle, and a divided foot of actions. Tolerant of the slim feed (teams
// carry only players[]) via useTimelineModels. Reusable wherever an "Up next"
// preview is shown (Home, chat/chatbot sidebars). For rich API fixtures with
// full edit/approval logic use FixtureCell instead.
const props = defineProps({
  fixture: {type: Object, default: () => ({})},
  // Render the built-in scheduling/score foot. Off + an #actions slot lets a
  // host supply its own foot (or none).
  showActions: {type: Boolean, default: true},
});

const route = useRoute();
const {user} = useAuthService();
const {fixtureResultActionLabel, fixtureResultActionIcon, fixtureScheduleState, fixtureScheduleActionLabel} = useValidation();
const {teamName, teamInitials, teamAvatar} = useTimelineModels();

const f = computed(() => props.fixture || {});
const homeName = computed(() => teamName(f.value.homeTeam));
const awayName = computed(() => teamName(f.value.awayTeam));

const format = computed(() => {
  const x = f.value;
  if (x?.leagueId || x?.leagueName) {
    return {name: [x.leagueName, x.leagueCycleDivisionName].filter(Boolean).join(' · '), icon: 'mdi-table', color: 'primary'};
  }
  if (x?.ladderId || x?.ladderName) return {name: x.ladderName || 'Ladder', icon: 'mdi-ladder', color: '#2477C8'};
  if (x?.tournamentId || x?.tournamentName) return {name: [x.tournamentName, x.tournamentStageName].filter(Boolean).join(' · '), icon: 'mdi-trophy', color: 'warning'};
  if (x?.americanoCycleDivisionId || x?.americanoId) return {name: [x.americanoName || 'Americano league', x.americanoRound ? `Round ${x.americanoRound}` : null].filter(Boolean).join(' · '), icon: 'mdi-account-switch', color: '#7C3AED'};
  if (x?.meetOccurrenceId) return {name: x.meetName || 'Meet', icon: 'mdi-handshake', color: 'success'};
  return {name: 'Friendly', icon: 'mdi-handshake-outline', color: 'textSubtle'};
});
const iconColor = computed(() => format.value.color.startsWith('#') ? format.value.color : `rgb(var(--v-theme-${format.value.color}))`);

const {outputDateFromDb} = useCrudConverters();
const scheduled = computed(() => f.value.scheduledAt || null);

// Who am I in this fixture (drives the brand-tint "me" avatar).
const myId = computed(() => user.value?.id);
const inHome = computed(() => (f.value.homeTeam?.players || []).some((p) => p.userId === myId.value));
const inAway = computed(() => (f.value.awayTeam?.players || []).some((p) => p.userId === myId.value));
const isAdmin = computed(() => f.value.clubId != null && (user.value?.adminFor || []).includes(f.value.clubId));

const hasResult = computed(() => f.value.homeTeamResult != null && f.value.awayTeamResult != null);
// Approval state can only be judged when the feed carries the approval
// timestamps; absent them, don't guess (else every scored fixture looks
// "awaiting approval"). Defaults to Edit score until the data is present.
const hasApprovalData = computed(() => 'homeTeamApprovedAt' in f.value || 'awayTeamApprovedAt' in f.value);
const homeApproved = computed(() => f.value.homeTeamApprovedAt != null);
const awayApproved = computed(() => f.value.awayTeamApprovedAt != null);
const fullyApproved = computed(() => homeApproved.value && awayApproved.value);
const mySideApproved = computed(() => (inHome.value && homeApproved.value) || (inAway.value && awayApproved.value));
const awaitingMyApproval = computed(() =>
    hasApprovalData.value && hasResult.value && !fullyApproved.value &&
    (isAdmin.value || ((inHome.value || inAway.value) && !mySideApproved.value)),
);

// Schedule state + wording come from the shared state machine in useValidation
// (mirrored by the mobile app) — do not hand-roll schedule labels here.
const myTeamId = computed(() =>
    inHome.value ? (f.value.homeTeamId ?? f.value.homeTeam?.id)
        : inAway.value ? (f.value.awayTeamId ?? f.value.awayTeam?.id) : null);
const scheduleState = computed(() => fixtureScheduleState(f.value, myTeamId.value));
const proposedDate = computed(() =>
    scheduled.value ? null : (f.value.homeTeamScheduledAt || f.value.awayTeamScheduledAt || null));

// Status chip per state. A fixture with a live proposal is NOT "needs scheduling" —
// that hid pending proposals and nudged users to re-suggest times already on the table.
const SCHEDULE_STATUS = {
  confirmed: {label: 'Confirmed', kind: 'ok', icon: 'mdi-calendar-check'},
  theirsPending: {label: 'Respond to proposed time', kind: 'warn', icon: 'mdi-calendar-clock'},
  minePending: {label: 'Awaiting opponent', kind: 'warn', icon: 'mdi-calendar-clock'},
  pending: {label: 'Awaiting confirmation', kind: 'warn', icon: 'mdi-calendar-clock'},
  none: {label: 'Needs scheduling', kind: 'warn', icon: 'mdi-calendar-clock'},
};
const status = computed(() => SCHEDULE_STATUS[scheduleState.value]);

const SCHEDULE_BUTTON_ICONS = {
  theirsPending: 'mdi-calendar-check', none: 'mdi-calendar-month',
  minePending: 'mdi-calendar-edit', confirmed: 'mdi-calendar-edit', pending: 'mdi-calendar-edit',
};
const scheduleButton = computed(() => ({
  label: fixtureScheduleActionLabel(scheduleState.value),
  icon: SCHEDULE_BUTTON_ICONS[scheduleState.value],
}));

// Score button: Record (no score) / Approve (score awaiting my approval) / Edit
// (score approved or awaiting the opponent). All open the score dialog. Membership
// here is judged via userId, so keep the local determination and route only the
// wording through the shared label/icon map for consistency with the other surfaces.
const resultAction = computed(() => {
  if (!hasResult.value) return 'record';
  if (awaitingMyApproval.value) return 'approve';
  return 'edit';
});
const scoreButton = computed(() => ({
  label: fixtureResultActionLabel(resultAction.value),
  icon: fixtureResultActionIcon(resultAction.value),
}));

// Show the proposed time too (marked pending by the status chip) — a card that says
// "TBC" while a concrete time awaits confirmation misrepresents the state.
const displayDate = computed(() => scheduled.value || proposedDate.value);
const timeTop = computed(() => displayDate.value ? outputDateFromDb(displayDate.value, 'EEE HH:mm') : 'TBC');
const timeBottom = computed(() => {
  if (!displayDate.value) return null;
  const date = outputDateFromDb(displayDate.value, 'd MMMM');
  return f.value.playingAreaName ? `${date} · ${f.value.playingAreaName}` : date;
});

const homeLevel = computed(() => f.value.homeTeam?.players?.[0]?.squashLevel);
const awayLevel = computed(() => f.value.awayTeam?.players?.[0]?.squashLevel);
const showLevel = (lvl) => lvl != null && lvl > 0;
</script>

<template>
  <div class="tl-card upnext">
    <router-link class="upnext-link" :to="`/fixtures/${fixture.id}`">
      <div class="upnext-meta">
        <span class="up-chip">
          <v-icon :icon="format.icon" size="14" :style="{ color: iconColor }"/>
          {{ format.name }}
        </span>
        <!-- Confirmed is the happy path — the time shown IS the plan, so no chip.
             Only pending states earn a status chip. -->
        <span v-if="scheduleState !== 'confirmed'" class="up-chip"
              :class="status.kind === 'ok' ? 'up-chip--ok' : 'up-chip--warn'">
          <v-icon :icon="status.icon" size="14"/>
          {{ status.label }}
        </span>
      </div>

      <div class="upnext-vs">
        <div class="upnext-player">
          <avatar-image-loader
              v-if="teamAvatar(fixture.homeTeam)"
              :image-file-name="teamAvatar(fixture.homeTeam)" :image-clickable="false" size="48" class="upnext-face"
          />
          <span v-else class="upnext-face" :class="{ 'upnext-face--me': inHome }">{{ teamInitials(fixture.homeTeam) }}</span>
          <div class="upnext-name">{{ homeName }}</div>
          <div v-if="showLevel(homeLevel)" class="upnext-level">{{ homeLevel }}</div>
        </div>

        <div class="upnext-time">
          <div class="upnext-time-top">{{ timeTop }}</div>
          <div v-if="timeBottom" class="upnext-time-bot">{{ timeBottom }}</div>
        </div>

        <div class="upnext-player">
          <avatar-image-loader
              v-if="teamAvatar(fixture.awayTeam)"
              :image-file-name="teamAvatar(fixture.awayTeam)" :image-clickable="false" size="48" class="upnext-face"
          />
          <span v-else class="upnext-face" :class="{ 'upnext-face--me': inAway }">{{ teamInitials(fixture.awayTeam) }}</span>
          <div class="upnext-name">{{ awayName }}</div>
          <div v-if="showLevel(awayLevel)" class="upnext-level">{{ awayLevel }}</div>
        </div>
      </div>
    </router-link>

    <div v-if="showActions || $slots.actions" class="upnext-foot">
      <slot name="actions">
        <!-- Open the global scheduling/score dialogs (BasePage) via route query. Hide
             (re)scheduling once a result exists — a played match can't be scheduled —
             and on meet fixtures, whose date always follows the meet occurrence. -->
        <v-btn v-if="!hasResult && fixture.meetOccurrenceId == null" :exact="true" variant="text" size="small" color="primary" :prepend-icon="scheduleButton.icon"
               :to="{ ...route, query: { ...route.query, schedulingFixtureId: fixture.id } }">{{ scheduleButton.label }}</v-btn>
        <v-spacer/>
        <v-btn :exact="true" variant="flat" size="small" color="primary" rounded="pill" :prepend-icon="scoreButton.icon"
               :to="{ ...route, query: { ...route.query, editingFixtureId: fixture.id } }">{{ scoreButton.label }}</v-btn>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upnext {
  padding: 16px;
  margin-bottom: 10px;
}

.upnext-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.upnext-meta {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

/* standalone status/context chip (mockup .chip) — self-contained, not a v-chip */
.up-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-charcoal));
  border: 1px solid rgb(var(--v-theme-borderLight));

  &--ok {
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
    border-color: transparent;
  }

  &--warn {
    background: rgb(var(--v-theme-warningTint));
    color: rgb(var(--v-theme-warning));
    border-color: transparent;
  }
}

.upnext-vs {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.upnext-player {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 7px;
}

.upnext-face {
  width: 48px;
  height: 48px;
  border-radius: 50% !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-bg));
  border: 1px solid rgb(var(--v-theme-borderLight));
  font-size: 15px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));

  &.upnext-face--me {
    background: rgb(var(--v-theme-brandTint));
    color: rgb(var(--v-theme-primary));
    border-color: transparent;
  }
}

.upnext-name {
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.upnext-level {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
}

.upnext-time {
  flex-shrink: 0;
  align-self: center;
  text-align: center;
  padding: 0 6px;
}

.upnext-time-top {
  font-size: 13px;
  font-weight: 700;
}

.upnext-time-bot {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}

.upnext-foot {
  display: flex;
  align-items: center;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
</style>
